import type { ApiProblem, ApiSchemas } from '@rental/contracts/client'

export type StaffSession = ApiSchemas['StaffSession']

/** Why the lock screen shows: `handback` when the customer's signing screen ended (CTR-2). */
export type LockReason = 'locked' | 'idle' | 'expired' | 'handback'

/**
 * Where the device stands, which decides the whole screen (ARCHITECTURE,
 * "Auth, devices and the shared counter iPad"):
 * - enroll: no device credential, or a revoked one; e-mail and password;
 * - locked: an enrolled device with nobody unlocked; the PIN pad;
 * - unlocked: one person's session; the app;
 * - signing: turned to the customer, who sees one contract and nothing else
 *   (CTR-2). No staff session exists then: the turn ended it.
 * - update: the API no longer accepts this version of the app (426). Nothing
 *   works until it is updated: the iPad app from the store, the web app by a
 *   reload.
 * The server decides every one of these; the app only follows its answers.
 */
export type Access =
  | { state: 'checking' }
  | { state: 'enroll'; reason: 'new' | 'revoked' }
  | { state: 'locked'; reason: LockReason }
  | { state: 'unlocked'; session: StaffSession }
  | { state: 'signing'; contractId: string; expiresAt: string }
  | { state: 'update' }

/**
 * What an API answer means for access, from any screen: a revoked or unknown
 * device goes back to enrollment, an ended session to the lock screen, an app
 * too old for the API to the update screen. Every other problem belongs to
 * the screen that asked (a wrong PIN, a full form).
 */
export function accessAfter(problem: ApiProblem): Access | null {
  if (problem.kind !== 'api') return null
  if (problem.status === 426 && problem.code === 'app_version_unsupported') {
    return { state: 'update' }
  }
  if (problem.status !== 401) return null
  switch (problem.code) {
    case 'device_revoked':
      return { state: 'enroll', reason: 'revoked' }
    case 'device_not_enrolled':
      return { state: 'enroll', reason: 'new' }
    case 'session_required':
      return { state: 'locked', reason: 'expired' }
    // The signing grant ended (used, expired or revoked): the iPad goes back to staff.
    case 'signing_required':
      return { state: 'locked', reason: 'handback' }
    default:
      return null
  }
}

/**
 * S9: a password someone else chose (a reset on Persone, or the command line)
 * while this person is unlocked. The server answers every request but the
 * change with 403 `password_change_required`, so the app shows the change at
 * once, on the same session, rather than that refusal on whichever screen
 * asked. Null for any other answer, and when the change is showing already.
 */
export function passwordChangeAfter(current: Access, problem: ApiProblem): Access | null {
  if (current.state !== 'unlocked' || current.session.passwordChangeRequired) return null
  if (problem.kind !== 'api' || problem.status !== 403) return null
  if (problem.code !== 'password_change_required') return null
  return { state: 'unlocked', session: { ...current.session, passwordChangeRequired: true } }
}

/**
 * True when moving to `next` unlocks the app: from any other state, or as
 * someone else. The same person's session told something new (a PIN saved,
 * S9's password to change, their own name) is not an unlock: the idle limit
 * does not count again from it (C2), nor does the lock screen's list move.
 */
export function isUnlock(current: Access, next: Access): boolean {
  if (next.state !== 'unlocked') return false
  return current.state !== 'unlocked' || current.session.user.id !== next.session.user.id
}

/**
 * True when moving to `next` would say nothing new: a "session ended" answer
 * reaching a device already locked, or back at enrollment. It answers a call
 * made before the lock, and the lock keeps its own reason (an idle lock says
 * so) rather than turning into "expired". On a version the API refuses,
 * nothing moves the screen: every call is refused the same way until the
 * update, and a late answer from before must not show the lock screen.
 */
export function addsNothing(current: Access, next: Access): boolean {
  if (current.state === 'update') return true
  if (next.state !== 'locked') return false
  // The customer's screen outlives the agent's session: a late "session ended" from
  // a call made before the turn must not close it. Only the grant's own end does.
  if (current.state === 'signing') return next.reason !== 'handback'
  return current.state === 'locked' || current.state === 'enroll'
}

/**
 * Data loaded for one person must never be shown to the next: on a shared
 * counter iPad, whoever unlocks after a Runner must not see the Runner's
 * cache, nor the other way round. True when the cache has to be emptied.
 */
export function mustForgetData(before: Access, after: Access): boolean {
  if (before.state !== 'unlocked') return false
  if (after.state !== 'unlocked') return true
  return before.session.user.id !== after.session.user.id
}

export function can(access: Access, permission: StaffSession['permissions'][number]): boolean {
  return access.state === 'unlocked' && access.session.permissions.includes(permission)
}
